import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { api } from "@/lib/api";
import { unwrapAxiosData } from "@/lib/api/query-utils";
import {
  buildUnitFamilyMap,
  type UnitFamilyMap,
} from "@/lib/parameters/parameter-units.utils";

const EMPTY_FAMILIES: UnitFamilyMap = new Map();

const keys = {
  propertyKinds: () => ["parameters", "property_kinds"] as const,
};

/**
 * Loads the backend's property-kind unit families (`GET /parameters/property_kinds`)
 * as a `property_kind_id -> units[]` map. The CMS configurator uses it to offer
 * a group's full unit family instead of only the units each parameter carries.
 * Falls back to an empty map until the fetch settles, so callers degrade to the
 * parameters' own units.
 */
export function usePropertyKindsQuery(enabled = true) {
  const query = useQuery({
    queryKey: keys.propertyKinds(),
    queryFn: () => unwrapAxiosData(api.parameters.listPropertyKinds()),
    enabled,
  });

  const unitFamilies = useMemo(
    () => (query.data ? buildUnitFamilyMap(query.data) : EMPTY_FAMILIES),
    [query.data],
  );

  return { unitFamilies, isPending: query.isPending, isError: query.isError };
}
